import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Format currency in Chilean Pesos
export function formatCurrency(amount) {
    if (amount === null || amount === undefined) return '$0';
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
}

// Format date (Chile timezone)
export function formatDate(date) {
    if (!date) return '-';
    let dateStr = date;
    
    // Only add 'Z' if the date string looks like a timestamp without timezone
    // Don't add Z if it already has a time component that's not midnight
    if (typeof date === 'string') {
        // If it's just a date (YYYY-MM-DD) or has T00:00:00, treat as local date
        if (date.match(/^\d{4}-\d{2}-\d{2}$/) || date.includes('T00:00:00')) {
            // Parse as local date to avoid timezone shift
            const parts = date.split('T')[0].split('-');
            const localDate = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
            return new Intl.DateTimeFormat('es-CL', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }).format(localDate);
        }
        // For timestamps, add Z if no timezone specified
        if (!date.endsWith('Z') && !date.includes('+')) {
            dateStr = date + 'Z';
        }
    }
    
    return new Intl.DateTimeFormat('es-CL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/Santiago'
    }).format(new Date(dateStr));
}

// Format datetime (Chile timezone)
export function formatDateTime(date) {
    if (!date) return '-';
    // Ensure the date is interpreted as UTC if no timezone is specified
    let dateStr = date;
    if (typeof date === 'string' && !date.endsWith('Z') && !date.includes('+')) {
        dateStr = date + 'Z';
    }
    return new Intl.DateTimeFormat('es-CL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Santiago'
    }).format(new Date(dateStr));
}

// Calculate days difference
export function daysDiff(date1, date2 = new Date()) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = d2 - d1;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// Get estado display info
export function getEstadoInfo(estado) {
    const estados = {
        // Estados iniciales - Azules
        servicio_en_curso: { label: 'En Curso', color: 'bg-blue-100 text-blue-700 border-blue-200' },
        servicio_terminado: { label: 'Terminado', color: 'bg-slate-100 text-slate-700 border-slate-200' },
        
        // Estados de proceso EDP - Púrpuras
        edp_enviado: { label: 'EDP Enviado', color: 'bg-purple-100 text-purple-700 border-purple-200' },
        espera_oc: { label: 'Espera OC', color: 'bg-violet-100 text-violet-700 border-violet-200' },
        edp_aprobado: { label: 'EDP APROBADO', color: 'bg-emerald-100 text-emerald-800 border-emerald-300 font-bold' },
        edp_rechazado: { label: 'EDP RECHAZADO', color: 'bg-red-100 text-red-800 border-red-300 font-bold' },
        
        // Estados de facturación - Cyans
        facturado: { label: 'Facturado', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
        
        // Estados de pago - Verdes y Amarillos
        pendiente_pago: { label: 'Pendiente Pago', color: 'bg-amber-100 text-amber-700 border-amber-200' },
        pago_parcial: { label: 'Pago Parcial', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
        pagado: { label: 'Pagado', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
        compromiso_pago: { label: 'Compromiso Pago', color: 'bg-lime-100 text-lime-700 border-lime-200' },
        
        // Estados problemáticos - Rojos y Naranjas
        vencido: { label: 'Vencido', color: 'bg-orange-100 text-orange-700 border-orange-200' },
        en_mora: { label: 'En Mora', color: 'bg-red-100 text-red-700 border-red-200' },
        cobranza_activa: { label: 'Cobranza Activa', color: 'bg-rose-100 text-rose-700 border-rose-200' },
        
        // Estados terminales negativos
        servicio_interrumpido: { label: 'Interrumpido', color: 'bg-gray-100 text-gray-700 border-gray-200' },
        castigado: { label: 'Castigado', color: 'bg-red-200 text-red-800 border-red-300' }
    };
    return estados[estado] || { label: estado, color: 'bg-slate-100 text-slate-600 border-slate-200' };
}

// Get rol display name
export function getRolDisplay(rol) {
    const roles = {
        superadmin: 'Super Administrador',
        admin_empresa: 'Administrador',
        finanzas: 'Finanzas',
        ejecutivo: 'Ejecutivo Comercial',
        solo_lectura: 'Solo Lectura'
    };
    return roles[rol] || rol;
}

// Truncate text
export function truncate(text, maxLength = 50) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
}

// ─────────────────────────────────────────────────────────────────────────────
// Chilean RUT formatting helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Formats a RUT as the user types: "771112223" → "77.111.222-3".
 * Accepts digits and a final K/k as the verifier digit. Returns the raw input
 * with non-RUT characters stripped if it can't be split (very short strings).
 */
export function formatRut(value) {
    if (!value) return '';
    // Keep digits + K
    const cleaned = String(value).replace(/[^0-9kK]/g, '').toUpperCase();
    if (cleaned.length === 0) return '';
    if (cleaned.length === 1) return cleaned;
    // Last char is the verifier digit
    const body = cleaned.slice(0, -1).replace(/^0+/, ''); // strip leading zeros
    const dv = cleaned.slice(-1);
    if (!body) return dv;
    // Insert thousands separators from the right
    const withDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${withDots}-${dv}`;
}

/** Returns only digits + K, useful for storing/validating */
export function cleanRut(value) {
    if (!value) return '';
    return String(value).replace(/[^0-9kK]/g, '').toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────────
// Safe error message extraction
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Safely extract a human-readable error message from an axios error.
 * Handles:
 *  - String detail (FastAPI HTTPException)
 *  - Array detail (FastAPI/Pydantic 422 validation errors: [{type, loc, msg, ...}])
 *  - Network errors / no response
 * Never returns a raw object — always returns a string suitable for toast.error.
 */
export function getErrorMessage(error, fallback = 'Ocurrió un error inesperado') {
    if (!error) return fallback;
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        // Pydantic validation errors
        const msgs = detail.map((e) => {
            const loc = Array.isArray(e?.loc) ? e.loc.filter((p) => p !== 'body').join('.') : '';
            const msg = e?.msg || 'Valor inválido';
            return loc ? `${loc}: ${msg}` : msg;
        });
        return msgs.join(' · ');
    }
    if (detail && typeof detail === 'object') {
        return detail.msg || detail.message || JSON.stringify(detail);
    }
    return error.response?.data?.message || error.message || fallback;
}

